import { useMemo, useState } from "react";
import { Activity, Coins, RefreshCw, Users, Wrench } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  useGetMcpUsageLogsQuery,
  useGetMcpUsageSummaryQuery,
} from "@/redux/api/usageAnalytics.rtkq";

const PAGE_SIZE = 25;
const ALL_TOOLS = "__all__";

const toStartOfDayIso = (date: string) => date ? new Date(`${date}T00:00:00`).toISOString() : undefined;
const toEndOfDayIso = (date: string) => date ? new Date(`${date}T23:59:59.999`).toISOString() : undefined;

const formatDateTime = (value: string) => new Intl.DateTimeFormat("he-IL", {
  dateStyle: "short",
  timeStyle: "medium",
}).format(new Date(value));

const McpUsagePanel = () => {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [email, setEmail] = useState("");
  const [toolName, setToolName] = useState(ALL_TOOLS);
  const [page, setPage] = useState(1);

  const filters = useMemo(() => ({
    from: toStartOfDayIso(dateFrom),
    to: toEndOfDayIso(dateTo),
    email: email.trim() || undefined,
    toolName: toolName === ALL_TOOLS ? undefined : toolName,
  }), [dateFrom, dateTo, email, toolName]);

  const {
    data: summary,
    isLoading: isSummaryLoading,
    isError: isSummaryError,
    refetch: refetchSummary,
  } = useGetMcpUsageSummaryQuery(filters);
  const {
    data: logs,
    isLoading: isLogsLoading,
    isError: isLogsError,
    refetch: refetchLogs,
  } = useGetMcpUsageLogsQuery({ ...filters, page, limit: PAGE_SIZE });

  const toolNames = useMemo(
    () => [...new Set(summary?.rows.map((row) => row.toolName) ?? [])].sort(),
    [summary?.rows],
  );
  const activeUsers = new Set(summary?.rows.map((row) => row.userId) ?? []).size;

  const handleFilterChange = (setter: (value: string) => void, value: string) => {
    setter(value);
    setPage(1);
  };
  const handleReset = () => {
    setDateFrom("");
    setDateTo("");
    setEmail("");
    setToolName(ALL_TOOLS);
    setPage(1);
  };
  const handleRefresh = () => {
    refetchSummary();
    refetchLogs();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wrench className="h-5 w-5" />
          שימוש MCP
        </CardTitle>
        <CardDescription>פירוט שימוש בכלי MCP, קרדיטים ומועדי הפעלה לפי משתמש</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <label className="text-sm text-muted-foreground" htmlFor="mcp-from">מתאריך</label>
            <Input id="mcp-from" type="date" value={dateFrom} onChange={(event) => handleFilterChange(setDateFrom, event.target.value)} className="w-40" />
          </div>
          <div className="space-y-1">
            <label className="text-sm text-muted-foreground" htmlFor="mcp-to">עד תאריך</label>
            <Input id="mcp-to" type="date" value={dateTo} onChange={(event) => handleFilterChange(setDateTo, event.target.value)} className="w-40" />
          </div>
          <div className="space-y-1">
            <label className="text-sm text-muted-foreground" htmlFor="mcp-email">משתמש</label>
            <Input id="mcp-email" type="search" placeholder="אימייל..." value={email} onChange={(event) => handleFilterChange(setEmail, event.target.value)} className="w-64" />
          </div>
          <div className="space-y-1">
            <label className="text-sm text-muted-foreground" htmlFor="mcp-tool">כלי</label>
            <Select value={toolName} onValueChange={(value) => handleFilterChange(setToolName, value)}>
              <SelectTrigger id="mcp-tool" className="w-64" aria-label="סינון לפי כלי MCP">
                <SelectValue placeholder="כל הכלים" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_TOOLS}>כל הכלים</SelectItem>
                {toolNames.map((name) => <SelectItem key={name} value={name}>{name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="sm" onClick={handleReset}>נקה</Button>
          <Button variant="outline" size="sm" onClick={handleRefresh} aria-label="רענון נתוני MCP">
            <RefreshCw className="h-4 w-4 ml-1" />
            רענן
          </Button>
        </div>

        {isSummaryError || isLogsError ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-destructive">
            טעינת נתוני MCP נכשלה. נסו לרענן את הדף.
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Card><CardContent className="p-4 text-center"><Activity className="mx-auto mb-1 h-5 w-5 text-muted-foreground" /><div className="text-2xl font-bold">{summary?.totals.calls.toLocaleString() ?? "—"}</div><div className="text-xs text-muted-foreground">הפעלות MCP</div></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><Coins className="mx-auto mb-1 h-5 w-5 text-muted-foreground" /><div className="text-2xl font-bold">{summary?.totals.creditsConsumed.toLocaleString() ?? "—"}</div><div className="text-xs text-muted-foreground">קרדיטים</div></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><Users className="mx-auto mb-1 h-5 w-5 text-muted-foreground" /><div className="text-2xl font-bold">{isSummaryLoading ? "—" : activeUsers.toLocaleString()}</div><div className="text-xs text-muted-foreground">משתמשים פעילים</div></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><Wrench className="mx-auto mb-1 h-5 w-5 text-muted-foreground" /><div className="text-2xl font-bold">{isSummaryLoading ? "—" : toolNames.length.toLocaleString()}</div><div className="text-xs text-muted-foreground">כלים בשימוש</div></CardContent></Card>
        </div>

        <div className="space-y-2">
          <h4 className="font-semibold">סיכום לפי משתמש וכלי</h4>
          <div className="overflow-x-auto rounded-lg border">
            <Table>
              <TableHeader><TableRow><TableHead className="text-right">משתמש</TableHead><TableHead className="text-right">משרד</TableHead><TableHead className="text-right">כלי</TableHead><TableHead className="text-right">הפעלות</TableHead><TableHead className="text-right">קרדיטים</TableHead><TableHead className="text-right">שימוש ראשון</TableHead><TableHead className="text-right">שימוש אחרון</TableHead></TableRow></TableHeader>
              <TableBody>
                {summary?.rows.map((row) => (
                  <TableRow key={`${row.userId}-${row.officeId}-${row.toolName}`}>
                    <TableCell><div className="font-medium">{row.displayName || "—"}</div><div className="text-xs text-muted-foreground">{row.email}</div></TableCell>
                    <TableCell>{row.officeName}</TableCell>
                    <TableCell><Badge variant="secondary" className="font-mono">{row.toolName}</Badge></TableCell>
                    <TableCell>{row.calls.toLocaleString()}</TableCell>
                    <TableCell>{row.creditsConsumed.toLocaleString()}</TableCell>
                    <TableCell className="whitespace-nowrap">{formatDateTime(row.firstUsedAt)}</TableCell>
                    <TableCell className="whitespace-nowrap">{formatDateTime(row.lastUsedAt)}</TableCell>
                  </TableRow>
                ))}
                {!isSummaryLoading && summary?.rows.length === 0 ? <TableRow><TableCell colSpan={7} className="py-8 text-center text-muted-foreground">אין נתוני MCP עבור הסינון שנבחר</TableCell></TableRow> : null}
              </TableBody>
            </Table>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold">יומן הפעלות</h4>
            <span className="text-sm text-muted-foreground">{logs?.pagination.total.toLocaleString() ?? 0} רשומות</span>
          </div>
          <div className="overflow-x-auto rounded-lg border">
            <Table>
              <TableHeader><TableRow><TableHead className="text-right">תאריך ושעה</TableHead><TableHead className="text-right">משתמש</TableHead><TableHead className="text-right">כלי</TableHead><TableHead className="text-right">קרדיטים</TableHead><TableHead className="text-right">כמות שימוש</TableHead><TableHead className="text-right">מזהה פעולה</TableHead></TableRow></TableHeader>
              <TableBody>
                {logs?.rows.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="whitespace-nowrap">{formatDateTime(log.createdAt)}</TableCell>
                    <TableCell><div className="font-medium">{log.displayName || "—"}</div><div className="text-xs text-muted-foreground">{log.email}</div></TableCell>
                    <TableCell><Badge variant="secondary" className="font-mono">{log.toolName}</Badge></TableCell>
                    <TableCell>{log.creditsConsumed.toLocaleString()}</TableCell>
                    <TableCell>{log.units.toLocaleString()}</TableCell>
                    <TableCell className="max-w-80 truncate font-mono text-xs" title={log.operationId}>{log.operationId || "—"}</TableCell>
                  </TableRow>
                ))}
                {!isLogsLoading && logs?.rows.length === 0 ? <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">אין רשומות להצגה</TableCell></TableRow> : null}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center justify-between">
            <Button variant="outline" size="sm" disabled={page <= 1 || isLogsLoading} onClick={() => setPage((current) => Math.max(current - 1, 1))}>הקודם</Button>
            <span className="text-sm text-muted-foreground">עמוד {logs?.pagination.page ?? page} מתוך {Math.max(logs?.pagination.totalPages ?? 1, 1)}</span>
            <Button variant="outline" size="sm" disabled={page >= (logs?.pagination.totalPages ?? 1) || isLogsLoading} onClick={() => setPage((current) => current + 1)}>הבא</Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default McpUsagePanel;
